import { api } from './api';

export interface ReportTypeDefinition {
  type: string;
  name: string;
  description: string;
  parameters: string[];
  sections?: string[];
}

export interface ReportSectionDefinition {
  section: string;
  name: string;
  description: string;
  filters: string[];
}

export interface ReportTypesResponse {
  report_types: ReportTypeDefinition[];
  sections?: ReportSectionDefinition[];
  formats: string[];
}

export interface ReportGenerationRequest {
  report_type: 'device_uptime' | 'content_playback' | 'system_activity' | 'custom';
  date_from: string;
  date_to: string;
  device_ids?: number[];
  media_ids?: number[];
  playlist_ids?: number[];
  include_details?: boolean;
  format?: 'json' | 'csv' | 'xlsx';
}

export interface DeviceUptimeReport {
  device_id: number;
  device_name: string;
  uptime_percentage: number;
  total_hours: number;
  downtime_hours: number;
  last_online?: string | null;
  status_changes: number;
  device_status?: string | null;
  playback_status?: string | null;
  last_heartbeat?: string | null;
  online_events?: number;
  offline_events?: number;
}

export interface ContentPlaybackReport {
  content_id: number;
  content_name: string;
  content_type: 'media' | 'playlist' | string;
  total_plays: number;
  total_duration: number;
  completion_rate: number;
  average_duration: number;
  unique_devices: number;
  completed_plays?: number;
  interrupted_plays?: number;
  first_played_at?: string | null;
  last_played_at?: string | null;
  total_duration_seconds?: number;
  average_duration_seconds?: number;
}

export interface SystemActivityReport {
  date: string;
  total_events: number;
  user_actions: number;
  device_events: number;
  playback_events: number;
  errors: number;
  active_devices: number;
  content_uploads: number;
  warnings?: number;
  critical_events?: number;
}

export interface ProofOfPlayRecord {
  id: number;
  device_id: number;
  media_id?: number | null;
  playlist_id?: number | null;
  schedule_event_id?: number | null;
  started_at: string;
  finished_at?: string | null;
  duration_planned?: number | null;
  duration_actual?: number | null;
  completed: boolean;
  interrupted: boolean;
  playback_data?: Record<string, unknown> | null;
  device_name?: string | null;
  media_title?: string | null;
  playlist_name?: string | null;
  duration_actual_minutes?: number | null;
  completion_rate?: number | null;
}

export interface ActivityLogRecord {
  id: number;
  timestamp: string;
  level: string;
  category: string;
  event_type: string;
  message: string;
  user_id?: number | null;
  device_id?: number | null;
  event_metadata?: Record<string, unknown> | null;
  ip_address?: string | null;
  user_agent?: string | null;
  related_object_type?: string | null;
  related_object_id?: number | null;
}

export interface PaginatedReportsResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ReportEnvelope<T> {
  report_type: string;
  period: string;
  data: T[];
  devices_count?: number;
  content_items?: number;
  days_count?: number;
}

export interface ReportGenerationResponse {
  id: string;
  report_type: string;
  date_from: string;
  date_to: string;
  generated_at: string;
  format: string;
  device_uptime?: DeviceUptimeReport[];
  content_playback?: ContentPlaybackReport[];
  system_activity?: SystemActivityReport[];
  custom_sections?: Record<string, unknown>;
  summary: Record<string, unknown>;
}

export interface ContentStatisticsResponse {
  period_days: number;
  total_media: number;
  total_media_files: number;
  total_playlists: number;
  storage_used: number;
  storage_used_mb: number;
  playback_count_30d: number;
  playbacks_24h: number;
  uploads_24h: number;
  by_type: Record<string, number>;
  content_distribution: {
    by_type: Record<string, number>;
    by_status: Record<string, number>;
    storage_by_type: Record<string, number>;
  };
  most_played_content: {
    media: ContentPlaybackReport[];
    playlists: ContentPlaybackReport[];
  };
  content_playback: ContentPlaybackReport[];
}

export interface MonitoringResponse {
  system_status: string;
  devices: {
    total: number;
    online: number;
    offline: number;
    online_percentage: number;
  };
  activity: {
    events_24h: number;
    errors_24h: number;
    error_rate: number;
  };
  content: {
    playbacks_24h: number;
    uploads_24h: number;
  };
  timestamp: string;
}

const withDefaultPeriod = (params?: Record<string, any>) => {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    date_from: from.toISOString(),
    date_to: now.toISOString(),
    ...(params || {}),
  };
};

export const reportsService = {
  getReportTypes() {
    return api.get<ReportTypesResponse>('/reports/types');
  },

  generateReport(payload: ReportGenerationRequest) {
    return api.post<ReportGenerationResponse>('/reports/generate', payload);
  },

  getActivityLogs(params?: Record<string, any>) {
    return api.get<PaginatedReportsResponse<ActivityLogRecord>>('/reports/activity-logs', { params });
  },

  getProofOfPlay(params?: Record<string, any>) {
    return api.get<PaginatedReportsResponse<ProofOfPlayRecord>>('/reports/proof-of-play', { params });
  },

  getDevicesUptime(params?: Record<string, any>) {
    return api.get<ReportEnvelope<DeviceUptimeReport>>('/reports/devices-uptime', { params: withDefaultPeriod(params) });
  },

  getContentPlayback(params?: Record<string, any>) {
    return api.get<ReportEnvelope<ContentPlaybackReport>>('/reports/content-playback', { params: withDefaultPeriod(params) });
  },

  getSystemActivity(params?: Record<string, any>) {
    return api.get<ReportEnvelope<SystemActivityReport>>('/reports/system-activity', { params: withDefaultPeriod(params) });
  },

  getTechnicalState(params?: Record<string, any>) {
    return api.get<Record<string, unknown>>('/reports/technical-state', { params: withDefaultPeriod(params) });
  },

  getContentStatistics(params?: Record<string, any>) {
    return api.get<ContentStatisticsResponse>('/reports/content-statistics', { params });
  },

  getMonitoring(params?: Record<string, any>) {
    return api.get<MonitoringResponse>('/reports/monitoring', { params });
  },

  exportActivityLogs(params?: Record<string, any>) {
    return api.get<string>('/reports/export/activity-logs', { params: { format: 'csv', ...(params || {}) } });
  },

  exportProofOfPlay(params?: Record<string, any>) {
    return api.get<string>('/reports/export/proof-of-play', { params: { format: 'csv', ...(params || {}) } });
  },

  exportReport(reportType: string, params?: Record<string, any>, format: 'csv' | 'json' | 'xlsx' = 'xlsx') {
    return api.get<Blob>(`/reports/export/${reportType}`, {
      params: { ...(params || {}), format },
      responseType: 'blob',
    });
  },
};

export default reportsService;
